import React, { useEffect, useState, ReactElement, useCallback, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  Box,
  Spinner,
  Container,
  Button,
  TextContent,
  SpaceBetween,
  Alert,
} from "@amzn/awsui-components-console";
import { Auth, Hub } from "aws-amplify";
import { setupAmplify } from "../../utils/auth";
import { setPool, USER_TYPE } from "../../utils/storage";
import { useSettings } from "../../hooks/useSettings";
import { AUTH_STATES } from "../../types";
import { getUserAtom, setUserAtom } from "../../contexts/userContext";
import {
  getSettingsAtom,
  setSettingsAtom,
} from "../../contexts/settingsContext";
import { AppConfigProvider } from "../../contexts/appConfigContext";
import "./Authenticate.css";

interface AuthenticateProps {
  children: ReactElement;
}

/**
 * Authentication Flow:
 * 1. Set up Hub listener FIRST (before Amplify config) - catches OAuth callbacks
 * 2. Configure Amplify with OAuth settings from settings.json
 * 3. Check for existing authenticated user
 * 4. If not authenticated, show sign-in button
 * 5. On sign-in, redirect to Cognito hosted UI
 * 6. Hub listener handles the callback and sets user state
 */
const Authenticate: React.FC<AuthenticateProps> = ({ children }) => {
  // Jotai state management
  const setLoggedInUser = useSetAtom(setUserAtom);
  const loggedInUser = useAtomValue(getUserAtom);
  const setSettings = useSetAtom(setSettingsAtom);
  const settings = useAtomValue(getSettingsAtom);

  // Local state
  const [authState, setAuthState] = useState<AUTH_STATES>(AUTH_STATES.SIGNING_IN);
  const [signInTriggered, setSignInTriggered] = useState<string>("");
  const [stage, setStage] = useState<string>("local");
  const [error, setError] = useState<string | null>(null);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  
  // Ref to track if Hub listener is set up (prevents race conditions)
  const hubListenerRef = useRef<(() => void) | null>(null);

  // Load settings
  const {
    settings: settingsConfig,
    loading: settingsLoading,
    error: settingsError,
  } = useSettings();

  /**
   * Parse Cognito user data and set user state
   */
  const parseCognitoUserData = useCallback(async (cognitoUserData: any) => {
    try {
      const rawAlias = cognitoUserData.getUsername();
      // Remove 'AmazonFederate_' prefix if present
      const actualAlias = rawAlias.startsWith('AmazonFederate_') 
        ? rawAlias.substring(15) 
        : rawAlias.split('_')[1] || rawAlias;

      const session = cognitoUserData.getSignInUserSession();
      const idToken = session?.getIdToken();
      const payload = idToken?.payload || {};

      const user = {
        ...cognitoUserData,
        username: actualAlias.toLowerCase(),
        attributes: {
          email: payload.email || payload['custom:EMAIL'],
          name: payload.given_name || payload.name || payload['custom:GIVEN_NAME'],
        },
        formattedAttributes: {
          username: actualAlias.toLowerCase(),
          givenName: payload.given_name || payload.name || payload['custom:GIVEN_NAME'],
          email: payload.email || payload['custom:EMAIL'],
          avatarUrl: `https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${actualAlias.toLowerCase()}`,
        }
      };

      console.info(`User authenticated: ${actualAlias}`);
      
      setLoggedInUser(user);
      setAuthState(AUTH_STATES.SIGNED_IN);
      setSignInTriggered("");

      // Clean up OAuth params from URL
      window.history.replaceState({}, "", window.location.pathname);
    } catch (err) {
      console.error("Error parsing Cognito user data:", err);
      throw err;
    }
  }, [setLoggedInUser]);

  /**
   * Execute federated sign-in
   */
  const executeFederateAuth = async () => {
    console.log("Initiating federated sign-in...");
    
    const currentUrl = window.location.pathname + window.location.search;

    setPool(USER_TYPE.INTERNAL);
    setSignInTriggered(USER_TYPE.INTERNAL);
    setAuthState(AUTH_STATES.SIGNING_IN);

    try {
      await Auth.federatedSignIn({
        customProvider: 'AmazonFederate',
        customState: currentUrl,
      });
    } catch (err: any) {
      // Redirect is expected - page navigates away
      console.log('Redirecting to authentication provider...');
    }
  };

  /**
   * Manual sign-in button handler
   */
  const onInternalUserSignIn = async () => {
    setError(null);
    await executeFederateAuth();
  };

  /**
   * STEP 1: Set up Hub listener FIRST
   * This MUST be set up before Amplify is configured to catch OAuth callbacks
   */
  useEffect(() => {
    console.log('Setting up Hub listener for auth events...');
    
    const hubListener = Hub.listen('auth', async ({ payload: { event, data } }) => {
      console.log('Hub auth event:', event, data);
      
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          // OAuth flow completed successfully - get the authenticated user
          console.log(`${event} event received - fetching user...`);
          try {
            const user = await Auth.currentAuthenticatedUser();
            await parseCognitoUserData(user);
          } catch (err) {
            console.error('Error getting user after auth event:', err);
            setError('Failed to get user information. Please try again.');
            setAuthState(AUTH_STATES.SIGNED_OUT);
          }
          break;
          
        case 'customOAuthState':
          // Custom state from OAuth redirect - restore original path
          console.log('customOAuthState:', data);
          try {
            // Data might be JSON stringified or plain string
            const path = data.startsWith('{') ? JSON.parse(data).path : data;
            if (path && path.startsWith('/') && path !== '/') {
              window.history.replaceState({}, "", path);
            }
          } catch {
            // If parsing fails, try using data directly
            if (data && data.startsWith('/') && data !== '/') {
              window.history.replaceState({}, "", data);
            }
          }
          break;
          
        case 'signOut':
          console.log('signOut event received');
          setLoggedInUser(null);
          setAuthState(AUTH_STATES.SIGNED_OUT);
          break;
          
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.error('Auth failure:', event, data);
          setError('Authentication failed. Please try again.');
          setAuthState(AUTH_STATES.SIGNED_OUT);
          setSignInTriggered("");
          // Clean up URL params on failure
          window.history.replaceState({}, "", window.location.pathname);
          break;
          
        default:
          break;
      }
    });

    // Store ref for cleanup
    hubListenerRef.current = hubListener;

    // Cleanup Hub listener on unmount
    return () => {
      if (hubListenerRef.current) {
        hubListenerRef.current();
        hubListenerRef.current = null;
      }
    };
  }, [parseCognitoUserData, setLoggedInUser]); // Empty deps would be ideal but we need these callbacks

  /**
   * STEP 2: Configure Amplify after Hub listener is set up
   */
  useEffect(() => {
    if (!settingsConfig || settingsLoading) return;

    // Check if authentication is disabled (local development)
    if (!settingsConfig.features?.enableAuthentication) {
      console.log('Authentication disabled - using mock user');
      setStage(settingsConfig.stage);
      if (!settings) {
        setSettings(settingsConfig);
      }
      setAuthState(AUTH_STATES.SIGNED_OUT);
      return;
    }

    const configureAuth = async () => {
      try {
        const { cognitoConfig, apiConfig } = settingsConfig;
        
        // Configure Amplify with OAuth settings
        await setupAmplify(cognitoConfig, apiConfig);
        console.log("Amplify configured successfully");
        
        setStage(settingsConfig.stage);
        if (!settings) {
          setSettings(settingsConfig);
        }
        setAmplifyConfigured(true);
      } catch (err) {
        console.error("Error configuring Amplify:", err);
        setError("Failed to initialize authentication. Please refresh the page.");
        setAuthState(AUTH_STATES.SIGNED_OUT);
      }
    };

    configureAuth();
  }, [settingsConfig, settingsLoading, settings, setSettings]);

  /**
   * STEP 3: Check for existing session or OAuth callback AFTER Amplify is configured
   */
  useEffect(() => {
    if (!amplifyConfigured) return;

    // Check if this is an OAuth callback (URL contains code parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');
    
    if (hasAuthCode) {
      console.log('OAuth callback detected - Hub listener will handle authentication...');
      // Don't check for existing session - let Hub listener handle the OAuth callback
      // Amplify automatically exchanges the code for tokens and fires Hub events
      return;
    }

    // No OAuth callback - check for existing authenticated session
    const checkExistingSession = async () => {
      try {
        console.log("Checking for existing session...");
        const user = await Auth.currentAuthenticatedUser();
        console.log("Found existing session for user");
        await parseCognitoUserData(user);
      } catch (err) {
        console.log("No existing session, showing sign-in UI");
        setAuthState(AUTH_STATES.SIGNED_OUT);
        setSignInTriggered("");
      }
    };

    checkExistingSession();
  }, [amplifyConfigured, parseCognitoUserData]);

  // Loading settings
  if (settingsLoading) {
    return (
      <Box textAlign="center" color="text-status-inactive" margin={{ vertical: "xxxl" }}>
        <Spinner size="normal" />
        <Box margin={{ top: "l" }}>Loading application settings...</Box>
      </Box>
    );
  }

  // Settings error
  if (settingsError || !settingsConfig) {
    return (
      <Box textAlign="center" margin={{ vertical: "xxxl" }}>
        <Alert type="error" header="Configuration Error">
          Failed to load application settings. Please refresh the page.
          {settingsError && (
            <Box margin={{ top: "s" }}>
              <small>Error: {settingsError.message}</small>
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  // Auth disabled - use mock user for local development
  if (!settingsConfig?.features?.enableAuthentication) {
    // Set mock user if not already set and mockUser config exists
    if (!loggedInUser && settingsConfig.mockUser) {
      const mockUser = {
        username: settingsConfig.mockUser.username,
        attributes: settingsConfig.mockUser.attributes,
        formattedAttributes: settingsConfig.mockUser.formattedAttributes,
      };
      setLoggedInUser(mockUser);
    }
    return <AppConfigProvider stage={stage}>{children}</AppConfigProvider>;
  }

  // Authentication in progress (before Amplify is configured or during sign-in)
  if ((authState === AUTH_STATES.SIGNING_IN && !error) || (!amplifyConfigured && !error)) {
    return (
      <Box textAlign="center" color="text-status-inactive" margin={{ vertical: "xxxl" }}>
        <Spinner size="normal" />
        <Box margin={{ top: "l" }}>Authenticating...</Box>
      </Box>
    );
  }

  // Not authenticated - show sign-in UI
  if (!loggedInUser) {
    return (
      <Container
        header={<Box textAlign="center" variant="h1">Welcome to ADDAPT</Box>}
      >
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)} header="Authentication Error">
            {error}
          </Alert>
        )}

        <Box textAlign="center" margin={{ bottom: "xl" }}>
          <SpaceBetween direction="vertical" size="m">
            <Button
              variant="primary"
              loading={signInTriggered === USER_TYPE.INTERNAL}
              disabled={signInTriggered === USER_TYPE.INTERNAL}
              onClick={onInternalUserSignIn}
            >
              Sign in with Midway
            </Button>
          </SpaceBetween>
        </Box>

        <Box textAlign="center" margin={{ top: "xxl" }}>
          <TextContent>
            <p className="footer-text">
              <small>
                By signing in, you agree to Amazon's terms and conditions. This
                application is for authorized use only.
              </small>
            </p>
          </TextContent>
        </Box>
      </Container>
    );
  }

  // Authenticated - render children
  return <AppConfigProvider stage={stage}>{children}</AppConfigProvider>;
};

export default Authenticate;
